import * as base from '../base';
import { PeopleSearchRequest } from '../../../models/overmind/search-request';
import { EmployeeSearchResponse } from '../../../models/view/employee-search-response';
import { GetUserDto, SaveUserDto } from '../../../models/view/employee';
import { Context } from '../..';
import { CompanyUserFileUpload } from '../../../models/overmind/company-user-file-upload';
import { CsvEmployeeResponse } from '../../../models/view/csv-employee-response';
import { UserBulkSaveDto, UserBulkValidationDto } from '../../../models/user-bulk-save';
import { NotificationPriority, NotificationType } from '../../../notifications/notifications.models';

const CONTROLLER = `people`;

export async function archivePerson(context: Context, learnerSlug: string): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `archive-employee${base.params({
      learnerSlug,
      companySlug: context.state.companyVariables.slug!
    })}`,), method: 'PUT'
  };
  await base.request(request);
}

export async function unarchivePerson(context: Context, learnerSlug: string): Promise<void> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `un-archive-employee${base.params({
      learnerSlug,
      companySlug: context.state.companyVariables.slug!
    })}`), method: 'POST'
  };
  await base.request(request);
}

export async function deletePerson(context: Context, learnerSlug: string): Promise<void> {
  const companySlug = context.state.companyVariables.slug;
  const request: base.IRequest = { url: base.apiURL(`${CONTROLLER}${base.params({ learnerSlug: learnerSlug, companySlug: companySlug })}`), method: 'DELETE' };
  const response = await base.request(request);

  if (!!response.error) {
    context.actions.addNotification({
      id: crypto.randomUUID(),
      title: 'Something went wrong',
      message: 'Our team has been notified. Please contact support for help.',
      priority: NotificationPriority.Toast,
      type: NotificationType.Error,
      active: true,
    });
  }
}

export async function bulkArchivePeople(context: Context, learnerSlugs: string[]): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `bulk-archive-employee`), method: 'PUT', body: JSON.stringify({ learnerSlugs: learnerSlugs }) };
  await base.request(request);
}

export async function bulkUnarchivePeople(context: Context, learnerSlugs: string[]): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `bulk-un-archive-employee`), method: 'POST', body: JSON.stringify({ learnerSlugs: learnerSlugs }) };
  await base.request(request);
}

export async function uploadEmployeesCsv(context: Context, companyUserFileUpload: CompanyUserFileUpload): Promise<CsvEmployeeResponse[] | undefined> {
  const formData: FormData = new FormData();
  formData.append('File', companyUserFileUpload.file);
  formData.append('AddToAssessment', companyUserFileUpload.addToAssessment ? 'true' : 'false');

  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `upload-employees`, companyUserFileUpload.companySlug), method: 'POST', headers: { Accept: 'multipart/form-data' }, body: formData };
  const response: base.IResponse<CsvEmployeeResponse[]> = await base.request(request);

  return response.data;
}

export async function searchPeople(context: Context, searchRequest: PeopleSearchRequest): Promise<EmployeeSearchResponse | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `search-employees`, `${searchRequest.companySlug}${base.params(searchRequest)}`) };
  const response: base.IResponse<EmployeeSearchResponse> = await base.request(request);

  return response.data;
}

export async function getPerson(context: Context, learnerSlug: string): Promise<GetUserDto | undefined> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `get-employee${base.params({
      learnerSlug: learnerSlug,
      companySlug: context.state.companyVariables.slug!
    })}`)
  };
  const response: base.IResponse<GetUserDto> = await base.request(request);
  return response.data;
}

export async function savePerson(context: Context, payload: SaveUserDto): Promise<string | undefined> {
  const url = base.apiURL(CONTROLLER, `save-employee`, context.state.companyVariables.slug!);
  const request: base.IRequest = { url, method: 'POST', body: JSON.stringify(payload) };
  const response: base.IResponse<string> = await base.request(request);

  if (!!response.error) {
    context.actions.addNotification({
      id: Date.now().toString(),
      title: 'Error',
      message: response.error.detail,
      priority: NotificationPriority.Toast,
      type: NotificationType.Error,
      active: true,
    });
    return undefined;
  }

  context.actions.addNotification({
    id: Date.now().toString(),
    title: `Person ${!!response.data ? 'edited' : 'created'}`,
    message: `${payload.firstName} ${payload.lastName} has successfully been ${!!payload.id ? 'edited' : 'created'}`,
    priority: NotificationPriority.Toast,
    type: NotificationType.Success,
    active: true,
  });
  return response.data;
}

export async function validateUsersUploadCsv(context: Context, companyUserFileUpload: CompanyUserFileUpload): Promise<UserBulkValidationDto[] | undefined> {
  const formData: FormData = new FormData();
  formData.append('File', companyUserFileUpload.file);

  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `validate-users-upload-csv`, companyUserFileUpload.companySlug),
    method: 'POST',
    headers: { Accept: 'multipart/form-data' },
    body: formData,
  };

  const response: base.IResponse<UserBulkValidationDto[]> = await base.request(request);

  return response.data;
}

export async function uploadEmployees(context: Context, companyUserFileUpload: CompanyUserFileUpload): Promise<CsvEmployeeResponse[] | undefined> {
  const formData: FormData = new FormData();
  formData.append('File', companyUserFileUpload.file);
  formData.append('AddToAssessment', companyUserFileUpload.addToAssessment ? 'true' : 'false');

  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `upload-employees`, companyUserFileUpload.companySlug), method: 'POST', headers: { Accept: 'multipart/form-data' }, body: formData };
  const response: base.IResponse<CsvEmployeeResponse[]> = await base.request(request);

  return response.data;
}

export async function uploadEmployeesData(context: Context, params: { companySlug: string; employeesData: UserBulkSaveDto[] }): Promise<UserBulkValidationDto[] | undefined> {
  const request: base.IRequest = {
    url: base.apiURL(CONTROLLER, `upload-employees`, params.companySlug),
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params.employeesData),
  };

  const response: base.IResponse<UserBulkValidationDto[]> = await base.request(request);

  return response.data;
}
